import { Link, useFetcher, useSearchParams } from "@remix-run/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-router-dom";
import type { action } from "~/routes/_marketing.sign-up";
import { Toast } from "./toast.component";
import { CrudTextField } from "./form/crud-form.component";
import { tvField } from "~/utils/global-tailwind-variants.utils";
import {
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { PasswordRequirements } from "./discovery/password-requirements";
import { Button } from "./button.component";
import { CTA } from "./cta.component";
import { Tooltip } from "./tooltip-overlay.component";

export function SignUpForm({
  ctaStyle = "button",
  showBorder = true,
  showPasswordRequirementsBelowField = true,
  showLoginLinkBottom = false,
  customRedirectTo,
  compact = false,
}: {
  ctaStyle?: "button" | "cta";
  showBorder?: boolean;
  showPasswordRequirementsBelowField?: boolean;
  showLoginLinkBottom?: boolean;
  customRedirectTo?: string;
  compact?: boolean;
}) {
  const fetcher = useFetcher<typeof action>();

  const actionData = useMemo(() => fetcher.data, [fetcher.data]);

  const [searchParams] = useSearchParams();
  const recaptchaResponseRef = useRef<HTMLInputElement | null>(null);

  const [showPassword, setShowPassword] = useState(false);

  const [password, setPassword] = useState("");

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    fetcher.submit(formData, {
      method: "post",
      action: "/sign-up",
      encType: "multipart/form-data",
    });
    return false;
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/api.js?render=6LeSGi4pAAAAAHsPHdCM8xT3Us4NGNYsSioOeyyA";
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      const grecaptcha = (window as any).grecaptcha;

      grecaptcha.ready(() => {
        grecaptcha
          .execute("6LeSGi4pAAAAAHsPHdCM8xT3Us4NGNYsSioOeyyA", {
            action: "login",
          })
          .then((token: string) => {
            if (recaptchaResponseRef.current) {
              recaptchaResponseRef.current.value = token;
            }
          });
      });
    };

    return () => {
      document.head.removeChild(script);
    };
  }, [recaptchaResponseRef, actionData]);

  const redirectTo = useMemo(
    () => customRedirectTo ?? searchParams.get("redirectTo") ?? undefined,
    [customRedirectTo, searchParams]
  );

  const gap = compact ? "4" : "6";

  return (
    <>
      {actionData !== undefined && (
        <Toast
          variant={actionData.success ? "success" : "error"}
          message={
            actionData.success
              ? "Success!"
              : actionData.errors?.general
              ? actionData.errors?.general[0]
              : "There were problems registering. Please try again."
          }
        />
      )}
      <Form className={`space-y-${gap}`} method="post" onSubmit={handleSubmit}>
        {redirectTo && (
          <input type="hidden" name="redirectTo" value={redirectTo} />
        )}
        {searchParams.get("intent") && (
          <input
            type="hidden"
            name="intent"
            value={searchParams.get("intent") ?? undefined}
          />
        )}
        <div
          className={`-space-y-p bg-white p-6 grid md:grid-cols-2 gap-${gap} ${
            showBorder ? "rounded-md shadow-sm border border-gray-100" : ""
          }`}
        >
          <div>
            <CrudTextField
              field={{
                label: "First Name *",
                name: "first_name",
                errors: actionData?.errors?.first_name ?? [],
                defaultValue: actionData?.fields?.first_name ?? undefined,
                type: "text",
              }}
            />
          </div>
          <div>
            <CrudTextField
              field={{
                label: "Last Name *",
                name: "last_name",
                errors: actionData?.errors?.last_name ?? [],
                defaultValue: actionData?.fields?.last_name ?? undefined,
                type: "text",
              }}
            />
          </div>

          <div>
            <CrudTextField
              field={{
                label: "Job Title *",
                name: "title",
                errors: actionData?.errors?.title ?? [],
                defaultValue: actionData?.fields?.title ?? undefined,
                type: "text",
              }}
            />
          </div>
          <div>
            <CrudTextField
              field={{
                label: "Company Name *",
                name: "company_name",
                errors: actionData?.errors?.company_name ?? [],
                defaultValue: actionData?.fields?.company_name ?? undefined,
                type: "text",
              }}
            />
          </div>
          <div>
            <CrudTextField
              field={{
                label: "Work Email Address *",
                name: "email",
                errors: actionData?.errors?.email ?? [],
                defaultValue: actionData?.fields?.email ?? undefined,
                type: "text",
              }}
            />
          </div>
          <div>
            <CrudTextField
              field={{
                label: "Phone *",
                name: "phone",
                errors: actionData?.errors?.phone ?? [],
                defaultValue: actionData?.fields?.phone ?? undefined,
                type: "text",
              }}
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="text-sm font-medium mb-2 flex items-center gap-x-2"
            >
              Password *
              {!showPasswordRequirementsBelowField && (
                <Tooltip
                  text={
                    <PasswordRequirements
                      password={password}
                      showColors={!!actionData?.errors?.password}
                      darkMode={true}
                    />
                  }
                  position="rightTop"
                >
                  <InformationCircleIcon
                    className={`h-5 ${
                      actionData?.errors?.password
                        ? "text-red-500"
                        : "text-gray-500"
                    }`}
                  />
                </Tooltip>
              )}
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                // required
                className={tvField({
                  className: "shadow-sm relative",
                  error: (actionData?.errors?.password?.length ?? 0) > 0,
                })}
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3 z-10"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <EyeSlashIcon
                    className="h-5 w-5 text-gray-400 hover:text-gray-500"
                    aria-hidden="true"
                  />
                ) : (
                  <EyeIcon
                    className="h-5 w-5 text-gray-400 hover:text-gray-500"
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
            {showPasswordRequirementsBelowField && (
              <PasswordRequirements
                password={password}
                showColors={!!actionData?.errors?.password}
              />
            )}
          </div>
          <div>
            <label
              htmlFor="passwordConfirmation"
              className="text-sm font-medium mb-2 block"
            >
              Confirm Password *
            </label>
            <input
              id="password-confirmation"
              name="passwordConfirmation"
              type="password"
              autoComplete="current-password"
              className={tvField({
                className: "shadow-sm relative",
                error:
                  (actionData?.errors?.passwordConfirmation?.length ?? 0) > 0,
              })}
              placeholder="Confirm Password"
              defaultValue={
                actionData?.fields?.passwordConfirmation ?? undefined
              }
            />
            <p
              className="text-red-500 mt-2 text-sm"
              id="errors-password-confirmation"
            >
              {actionData?.errors?.passwordConfirmation?.join(", ")}
            </p>
          </div>
        </div>
        <div>
          <div id="inline-badge"></div>
        </div>
        <input
          type="hidden"
          id="g-recaptcha-response"
          name="g-recaptcha-response"
          ref={recaptchaResponseRef}
        />
        <div className="flex justify-end pr-6">
          {showLoginLinkBottom && (
            <div className="text-gray-400 text-sm flex items-center gap-x-2 ml-6 mr-auto">
              Already using Revyse?{" "}
              <Link
                to={`/login?redirectTo=${redirectTo ?? ""}`}
                className="text-sky-500"
              >
                Login
              </Link>
            </div>
          )}
          {ctaStyle === "button" && (
            <Button type="submit">
              {fetcher.state === "submitting" ? "Signing Up..." : "Sign Up"}
            </Button>
          )}
          {ctaStyle === "cta" && (
            <CTA type="submit">
              {fetcher.state === "submitting" ? "Signing Up..." : "Sign Up"}
            </CTA>
          )}
        </div>
      </Form>
    </>
  );
}
